import { toZonedTime } from 'date-fns-tz';

import { useLocalStorage } from '@bonusly/storage';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useHomepageRedesignEnabled } from '../../BonusFeed/hooks/useHomepageRedesignEnabled';
import { useOneOnOnesAccess } from './use-one-on-ones-access';

export interface ConfettiLocalData {
	dateShown?: Date;
}

const RECOTNITION_LOCAL_STORAGE_KEY = 'bonusly-recognition-confetti';
const FEEDBACK_LOCAL_STORAGE_KEY = 'bonusly-feedback-confetti';

/**
 * checks the local storage data, if the current date (from the company's timezone) is greater than the one stored
 * and the date stored is on a different month, we need to clear the local storage entry and allow the confetti to show
 * otherwise, the confetti will not show
 * @returns TRUE if the confetti should show, false otherwise
 */
const shouldDisplayConfetti = (userLocalTime: Date, localStorageData: ConfettiLocalData, progress: number) => {
	if (progress !== 100) {
		return false;
	}

	if (!localStorageData?.dateShown) {
		return true;
	}

	const confettiShown = new Date(localStorageData.dateShown);

	if (isNaN(confettiShown.getTime())) {
		return true;
	}

	const hasSeenConfettiThisMonth = userLocalTime.getTime() > confettiShown.getTime() && userLocalTime.getMonth() === confettiShown.getMonth();

	return !hasSeenConfettiThisMonth;
};

interface UseDispalyConfettiInput {
	feedbackProgress: number;
	recognitionProgress: number;
}

interface UseDispalyConfettiResponse {
	setFeedbackLocalStorageData: (data: ConfettiLocalData) => void;
	setRecognitionLocalStorageData: (data: ConfettiLocalData) => void;
	shouldDisplayFeedbackConfetti: boolean;
	shouldDisplayRecognitionConfetti: boolean;
}

export const useDisplayConfetti = ({ feedbackProgress, recognitionProgress }: UseDispalyConfettiInput): UseDispalyConfettiResponse => {
	const user = useCurrentUser();
	const timezone = user?.time_zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
	const userLocalTime = toZonedTime(new Date(), timezone);

	// Recognition Confetti
	const [recognitionLocalStorageData, setRecognitionLocalStorageData] = useLocalStorage<ConfettiLocalData>(RECOTNITION_LOCAL_STORAGE_KEY, {});
	const shouldDisplayRecognitionConfetti = shouldDisplayConfetti(userLocalTime, recognitionLocalStorageData || {}, recognitionProgress);

	// Feedback Confetti
	const { canAccessOneOnOnes } = useOneOnOnesAccess();
	const [feedbackLocalStorageData, setFeedbackLocalStorageData] = useLocalStorage<ConfettiLocalData>(FEEDBACK_LOCAL_STORAGE_KEY, {});
	const shouldDisplayFeedbackConfetti = shouldDisplayConfetti(userLocalTime, feedbackLocalStorageData || {}, feedbackProgress);
	const homepageRedesignEnabled = useHomepageRedesignEnabled();

	return {
		setFeedbackLocalStorageData,
		setRecognitionLocalStorageData,
		shouldDisplayFeedbackConfetti: canAccessOneOnOnes && !homepageRedesignEnabled && shouldDisplayFeedbackConfetti,
		shouldDisplayRecognitionConfetti,
	};
};
